/**
 * Whist bot.
 *
 * Classic signal-free Whist play, from the bot's own view only:
 *  - Leading: top of a sequence (or the ace/king) from the longest side suit; with a long trump holding it draws
 *    trumps with the top of a sequence; otherwise the fourth-best card of the longest suit.
 *  - Second hand low, third hand high (the cheapest card that beats the table), fourth hand wins as cheaply as
 *    possible unless the partner already has the trick.
 *  - Void in the led suit: ruff with the lowest sufficient trump when the opponents are winning, else discard the
 *    lowest card of the weakest side suit. HARD/EXPERT/NORMAL track played cards to spot master cards.
 */
import { buildDeck, parseCard, SUITS, type CardId, type Suit } from '../../core/cards.js';
import { chance, type BotContext, type BotEngine } from '../../bots/types.js';
import { whistPartnerOf, whistTrickWinner, type WhistAction, type WhistLegal, type WhistTrickPlay, type WhistView } from './engine.js';

const rankOf = (id: CardId) => parseCard(id).rank;
const suitOf = (id: CardId) => parseCard(id).suit;

const lowestOf = (cards: readonly CardId[], trump: Suit) =>
  [...cards].sort((a, b) => (suitOf(a) === trump ? 100 : 0) + rankOf(a) - ((suitOf(b) === trump ? 100 : 0) + rankOf(b)))[0]!;
const highestOf = (cards: readonly CardId[]) => [...cards].sort((a, b) => rankOf(b) - rankOf(a))[0]!;

interface Knowledge {
  unseen: Set<CardId>;
}

function buildKnowledge(view: WhistView, ctx: BotContext): Knowledge {
  const seen = new Set<CardId>(view.hand);
  view.trick.forEach((p) => seen.add(p.card));
  const remembered = ctx.profile.memory;
  view.tricks.forEach((t, i) => {
    // Forgetful bots only recall the most recent tricks.
    if (remembered >= 1 || i >= view.tricks.length - Math.max(1, Math.round(view.tricks.length * remembered))) {
      for (const p of t.plays) seen.add(p.card);
    }
  });
  return { unseen: new Set(buildDeck().filter((c) => !seen.has(c))) };
}

function isMaster(card: CardId, k: Knowledge): boolean {
  const c = parseCard(card);
  for (const u of k.unseen) {
    const x = parseCard(u);
    if (x.suit === c.suit && x.rank > c.rank) return false;
  }
  return true;
}

function wouldWin(trick: readonly WhistTrickPlay[], seat: number, card: CardId, trump: Suit): boolean {
  if (trick.length === 0) return true;
  return whistTrickWinner([...trick, { seat, card }], trump) === seat;
}

/** Groups cards by suit, highest first. */
function bySuit(cards: readonly CardId[]): Map<Suit, CardId[]> {
  const m = new Map<Suit, CardId[]>();
  for (const s of SUITS) m.set(s, []);
  for (const c of cards) m.get(suitOf(c))!.push(c);
  for (const list of m.values()) list.sort((a, b) => rankOf(b) - rankOf(a));
  return m;
}

function lead(view: WhistView, legal: CardId[], k: Knowledge | null): CardId {
  const trump = view.trump;
  const groups = bySuit(legal);
  const side = [...groups.entries()].filter(([s, l]) => s !== trump && l.length > 0);
  const trumps = groups.get(trump)!;

  // Cash a known master in a side suit.
  if (k) {
    const masters = side.flatMap(([, l]) => l).filter((c) => isMaster(c, k));
    if (masters.length) return highestOf(masters);
  }
  // Long, strong trumps: draw them with the top of a sequence.
  if (trumps.length >= 5 && rankOf(trumps[0]!) >= 12) return trumps[0]!;

  const pick = (list: CardId[]): CardId => {
    const top = list[0]!;
    const second = list[1];
    if (rankOf(top) >= 13 || (second && rankOf(top) - rankOf(second) === 1 && rankOf(top) >= 11)) return top;
    // Fourth best of a long suit, else the lowest.
    return list.length >= 4 ? list[3]! : list[list.length - 1]!;
  };

  if (side.length) {
    const ordered = [...side].sort((a, b) => b[1].length - a[1].length || rankOf(b[1][0]!) - rankOf(a[1][0]!));
    return pick(ordered[0]![1]);
  }
  return pick(trumps.length ? trumps : legal);
}

function follow(view: WhistView, legal: CardId[], k: Knowledge | null): CardId {
  const me = view.seat!;
  const trump = view.trump;
  const trick = view.trick;
  const partner = whistPartnerOf(me);
  const winnerSeat = whistTrickWinner(trick, trump);
  const winnerCard = trick.find((p) => p.seat === winnerSeat)!.card;
  const last = trick.length === 3;
  const ledSuit = suitOf(trick[0]!.card);
  const following = legal.every((c) => suitOf(c) === ledSuit);
  const winners = legal.filter((c) => wouldWin(trick, me, c, trump));
  const cheapest = (cards: CardId[]) => lowestOf(cards, trump);

  if (winnerSeat === partner) {
    // Partner has it: take it over only if the lead is shaky and someone behind can still beat it.
    const safe = last || (k ? isMaster(winnerCard, k) && suitOf(winnerCard) === ledSuit : rankOf(winnerCard) >= 13);
    if (safe || winners.length === 0) return cheapest(legal);
    if (following && trick.length === 2 && rankOf(winnerCard) < 10) return cheapest(winners);
    return cheapest(legal);
  }

  if (following) {
    if (winners.length === 0) return cheapest(legal);
    // Second hand low; third and fourth hand win as cheaply as possible.
    if (trick.length === 1) {
      const sure = k ? winners.filter((c) => isMaster(c, k)) : [];
      return sure.length ? cheapest(sure) : cheapest(legal);
    }
    return cheapest(winners);
  }

  // Void in the led suit.
  if (winners.length > 0) {
    const worth = last || trick.length === 2 || rankOf(winnerCard) >= 12 || suitOf(winnerCard) === trump;
    if (worth) return cheapest(winners);
  }
  // Discard the lowest card of the weakest side suit, keeping trumps.
  const groups = bySuit(legal.filter((c) => suitOf(c) !== trump));
  const weakest = [...groups.entries()].filter(([, l]) => l.length > 0).sort((a, b) => a[1].length - b[1].length || rankOf(a[1][0]!) - rankOf(b[1][0]!))[0];
  if (weakest) return weakest[1][weakest[1].length - 1]!;
  return cheapest(legal);
}

export function whistBotChoose(view: WhistView, legal: CardId[], ctx: BotContext): CardId {
  if (legal.length === 1) return legal[0]!;
  const k = ctx.profile.memory > 0 ? buildKnowledge(view, ctx) : null;
  return view.trick.length === 0 ? lead(view, legal, k) : follow(view, legal, k);
}

export const whistBot: BotEngine<WhistView, WhistLegal, WhistAction> = {
  decideAction(view, legal, ctx) {
    if (legal.kind === 'play') return this.chooseCard!(view, legal, ctx);
    throw new Error('Bot asked to act without legal actions');
  },

  chooseCard(view, legal, ctx) {
    if (legal.kind !== 'play') return this.handleTimeout(view, legal, ctx);
    const cards = legal.cards;
    if (cards.length === 1) return { type: 'play', card: cards[0]! };
    if (chance(ctx, ctx.profile.mistakeRate)) return { type: 'play', card: cards[ctx.rng.int(cards.length)]! };
    const card = whistBotChoose(view, cards, ctx);
    return { type: 'play', card: cards.includes(card) ? card : cards[0]! };
  },

  evaluateState(view) {
    const me = view.seat;
    if (me === null) return 0;
    const team = me % 2;
    const diff = view.teamScores[team]! - view.teamScores[1 - team]!;
    return Math.max(0, Math.min(1, 0.5 + diff / 20));
  },

  calculateRisk() {
    return 0;
  },

  handleTimeout(view, legal) {
    if (legal.kind === 'play') return { type: 'play', card: lowestOf(legal.cards, view.trump) };
    throw new Error('No legal action');
  },
};
